/**
 * Single source of truth for prompt-block-category presentation in the
 * Workshop (spec: taxonomy reconcile). Every field label / hint / example
 * and the which-section grouping lives here so the render-from-data
 * sections and the coach proposal card can never drift.
 *
 * Hints are functions of the persona name and MUST be pronoun-free (§4.8
 * name-led copy). Examples are deliberately neutral.
 */
import type { BlockCategory } from '@/stores/composerStore'

export type BlockGroup = 'who' | 'how' | 'rules' | 'know' | 'extra'

export interface BlockFieldMeta {
  label: string
  hint: (name: string) => string
  example: string
  group: BlockGroup
  emoji: string
  galleryDescription: string
  placeholder?: string
  addable?: boolean // default true; false = not offered in the Add-block gallery
}

export const BLOCK_TAXONOMY: Record<BlockCategory, BlockFieldMeta> = {
  identity: {
    label: 'Identity',
    hint: (name) => `${name}'s role, the business represented, and what ${name} knows.`,
    example:
      'You are Sam, the friendly order assistant for a local gift shop. You know the full product catalog, prices, and pickup/delivery options.',
    group: 'who',
    emoji: '🪪',
    galleryDescription: 'Who {name} is and what they know.',
    // Identity is a singular starter (the Who section has no "+ Add block");
    // excluding it from the gallery avoids a confusing second "Identity (2)" card.
    addable: false,
  },
  trait: {
    label: 'Personality traits',
    hint: (name) => `3–5 short traits that shape how ${name} shows up.`,
    example: 'Warm · patient · detail-oriented · lightly playful · never pushy',
    group: 'how',
    emoji: '🎭',
    galleryDescription: 'A few adjectives that shape how they show up.',
  },
  tone: {
    label: 'Tone of voice',
    hint: (name) => `How ${name} actually speaks — formality, warmth, sentence length, emoji.`,
    example:
      "Short, warm sentences. Uses the customer's name. A little Spanish warmth (¡claro!). At most one emoji per message.",
    group: 'how',
    emoji: '🗣️',
    galleryDescription: 'How they phrase things — warmth, length, emoji.',
  },
  behavioral: {
    label: 'Behavior',
    hint: (name) => `What ${name} does, avoids, or always does during a conversation.`,
    example: 'Always confirm order details before sending a payment link. Never invent a price.',
    group: 'how',
    emoji: '🤝',
    galleryDescription: 'What they always / never do mid-conversation.',
  },
  constraints: {
    label: 'Rules & constraints',
    hint: (name) => `Hard limits ${name} must always respect.`,
    example: 'Never share internal pricing formulas. Never promise same-day delivery after 2pm.',
    group: 'rules',
    emoji: '📐',
    galleryDescription: 'Hard limits they must always respect.',
  },
  domain: {
    label: 'Domain knowledge',
    hint: () => 'Background facts and reference material used to ground answers.',
    example: 'Store hours: Mon–Sat 9–7. Delivery zones: city center, north side, east side.',
    group: 'know',
    emoji: '📚',
    galleryDescription: 'Reference facts used to ground answers.',
  },
  output_format: {
    label: 'Output format',
    hint: (name) => `How ${name} structures replies — length, formatting, structure.`,
    example: 'Reply in short WhatsApp-style messages. Use bullet lists only for product options.',
    group: 'extra',
    emoji: '📤',
    galleryDescription: 'Reply length, structure, and formatting.',
  },
  examples: {
    label: 'Examples',
    hint: () => 'Sample exchanges that show the desired behavior.',
    example: 'Customer: "¿Hacen entregas a domicilio?" → "¡Claro! Entregamos…"',
    group: 'know',
    emoji: '💬',
    galleryDescription: 'Sample exchanges showing the desired behavior.',
  },
  freeform: {
    label: 'Additional instructions',
    hint: () => "Anything else that doesn't fit the categories above.",
    example: 'Escalate to a human if the customer reports a problem with a previous order.',
    group: 'extra',
    emoji: '✏️',
    galleryDescription: "Anything else that doesn't fit a category.",
  },
  never_forget: {
    label: 'Never forget',
    hint: (name) => `The 2–3 things ${name} must never lose sight of.`,
    example: 'Always confirm the final total before sending a payment link.',
    group: 'rules',
    emoji: '⭐',
    galleryDescription: 'Must-remembers, pinned near the top of the prompt.',
    placeholder: 'e.g. Always confirm the total before sending a payment link.',
  },
  escalation: {
    label: 'Escalation conditions',
    hint: (name) => `When ${name} should hand off to a human, and how.`,
    example:
      'If the customer reports a damaged item or asks for a refund, escalate to a human and stop taking the order.',
    group: 'rules',
    emoji: '🚨',
    galleryDescription: 'When to hand off to a human.',
    placeholder: 'e.g. If the customer asks for a refund, escalate to a human.',
  },
  do_examples: {
    label: 'Do examples',
    hint: (name) => `Sample messages that show exactly how ${name} should respond.`,
    example:
      'Customer: "¿Precio del paquete de regalo grande?" → "¡Claro! Cuesta $480 🎁 ¿Para qué día lo necesitas?"',
    group: 'know',
    emoji: '✅',
    galleryDescription: 'Sample messages to imitate.',
    placeholder: 'Customer: "…" → "…"',
  },
  dont_examples: {
    label: "Don't examples",
    hint: (name) => `Sample messages ${name} should avoid — and steer away from.`,
    example: 'Avoid: "No sé, déjame checar." Instead, offer a concrete option or a next step.',
    group: 'know',
    emoji: '⛔',
    galleryDescription: 'Sample messages to avoid.',
    placeholder: 'Avoid: "…" — instead, "…"',
  },
}

function titleCase(raw: string): string {
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Field label, suffixed " (2)", " (3)"… for repeated same-category blocks. */
export function blockFieldLabel(category: string, ordinal: number): string {
  const base =
    (BLOCK_TAXONOMY as Record<string, BlockFieldMeta>)[category]?.label ?? titleCase(category)
  return ordinal === 0 ? base : `${base} (${ordinal + 1})`
}

/** Resolved label/hint/example for a category, with a generic fallback for unknowns. */
export function blockFieldMeta(
  category: string,
  name: string,
): { label: string; hint: string; example: string } {
  const meta = (BLOCK_TAXONOMY as Record<string, BlockFieldMeta>)[category]
  if (!meta) {
    return { label: titleCase(category), hint: `Custom "${category}" instructions for ${name}.`, example: '' }
  }
  return { label: meta.label, hint: meta.hint(name), example: meta.example }
}

/** Emoji for a category, with a generic fallback for unknown/custom ones. */
export function blockEmoji(category: string): string {
  return (BLOCK_TAXONOMY as Record<string, BlockFieldMeta>)[category]?.emoji ?? '🧩'
}
